import { LitElement, html, css } from 'lit'
import { StoreMixin, store } from '../store.js'
import { navigate } from '../router.js'
import { api, clearAccessToken } from '../api.js'
import * as lookups from '../lookups.js'
import { toast } from './app-toast.js'
import { brandLogo } from './brand-logo.js'

// App header, restyled to the BrandExtract design system: a sticky eggplant bar
// carrying the BrandExtract logotype with a chartreuse "Ziggy" eyebrow, and the
// signed-in user as a pill on the right that opens the account menu.
class AppNav extends StoreMixin(LitElement) {
  static properties = { _menuOpen: { state: true } }

  static styles = css`
    :host { display: block; position: sticky; top: 0; z-index: 20; }

    nav { display: flex; align-items: center; gap: 40px; height: 72px; padding: 0 40px;
          background: var(--eggplant-500); color: var(--neutral-50); }

    .brand { display: flex; align-items: center; gap: 14px; margin-right: auto;
             color: var(--white); cursor: pointer; }
    .brand svg { height: 34px; width: auto; display: block; }
    .brand .tag { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700;
                  letter-spacing: 0.16em; text-transform: uppercase; color: var(--chartreuse-500);
                  padding-left: 14px; border-left: 1px solid rgba(255,255,255,.22);
                  position: relative; top: 5px; }

    .account { position: relative; flex-shrink: 0; }
    .avatar { display: flex; align-items: center; gap: 10px; cursor: pointer;
              background: rgba(255,255,255,.1); border: none; border-radius: 999px;
              padding: 5px 14px 5px 5px; color: var(--white); font: inherit; }
    .avatar img { width: 30px; height: 30px; border-radius: 50%; display: block; }
    .avatar .initial { width: 30px; height: 30px; border-radius: 50%; display: grid;
                       place-items: center; background: var(--chartreuse-500);
                       color: var(--eggplant-500); font-weight: 800; font-size: 13px; }
    .avatar .name { font-size: 14px; font-weight: 500; }

    .menu { position: absolute; right: 0; top: 52px; min-width: 210px; background: var(--white);
            color: var(--eggplant-500); border: 1px solid var(--neutral-100); border-radius: 8px;
            box-shadow: var(--shadow-menu); overflow: hidden; z-index: 50; }
    .menu .who { padding: 14px 16px 4px; font-family: var(--font-eyebrow); font-size: 10px;
                 letter-spacing: 0.12em; text-transform: uppercase; color: var(--neutral-500); }
    .menu .whoami { padding: 0 16px 12px; font-weight: 700; }
    .menu .rule { height: 1px; background: var(--neutral-100); }
    .menu button, .menu a { display: block; width: 100%; box-sizing: border-box; text-align: left;
                            padding: 12px 16px; background: none; border: none; font: inherit;
                            cursor: pointer; color: var(--eggplant-500); text-decoration: none; }
    .menu button:hover, .menu a:hover { background: var(--neutral-50); }

    /* The full name is the first thing to go when the bar gets tight — the
       avatar disc still identifies the account and opens the same menu. */
    @media (max-width: 560px) {
      nav { padding: 0 20px; gap: 20px; }
      .avatar .name { display: none; }
      .avatar { padding: 5px; }
    }
  `

  _toggle() { this._menuOpen = !this._menuOpen }

  async _logout() {
    this._menuOpen = false
    try { await api.post('/auth/logout', {}) } catch {}
    clearAccessToken()
    // navigate() is pushState — no page reload — so the lookup caches would
    // otherwise survive into the next user's session in this tab and show them
    // the previous user's clients, projects and (per-user) service list.
    lookups.reset()
    store.clear()
    navigate('/login')
  }

  // Admin-only: force a re-download of the shared Workamajig clients/projects
  // catalog (e.g. to recover after a bad sync blanked it).
  async _refreshCatalog() {
    this._menuOpen = false
    toast('Refreshing Workamajig catalog…', 'info')
    try {
      const res = await api.post('/workamajig/catalog/refresh')
      toast(`Workamajig catalog refreshed — ${res.clients} clients, ${res.jobs} projects.`, 'success')
    } catch (err) {
      toast(err.message || 'Catalog refresh failed.', 'error')
    }
  }

  render() {
    const { user } = this.appState
    if (!user) return html``
    const initial = (user.fullName || user.email || '?').trim().charAt(0).toUpperCase()
    return html`
      <nav>
        <span class="brand" @click=${() => navigate('/')}>
          ${brandLogo(34)}
          <span class="tag">Ziggy</span>
        </span>
        <div class="account">
          <button class="avatar" @click=${this._toggle}>
            ${user.avatarUrl
              ? html`<img src=${user.avatarUrl} alt="" />`
              : html`<span class="initial">${initial}</span>`}
            <span class="name">${user.fullName || user.email}</span>
          </button>
          ${this._menuOpen ? html`
            <div class="menu">
              <div class="who">Signed in as</div>
              <div class="whoami">${user.fullName || user.email}</div>
              <div class="rule"></div>
              <a href="https://app21.workamajig.com/platinum/?aid=user.timeEntry.edit&k=0"
                 target="_blank" rel="noopener noreferrer"
                 @click=${() => { this._menuOpen = false }}>Open Workamajig</a>
              <button @click=${() => { this._menuOpen = false; navigate('/settings') }}>Settings</button>
              ${user.isAdmin ? html`
                <button @click=${() => { this._menuOpen = false; navigate('/reports') }}>Reports</button>
                <button @click=${this._refreshCatalog}>Refresh Workamajig catalog</button>` : ''}
              <button @click=${this._logout}>Sign out</button>
            </div>` : ''}
        </div>
      </nav>
    `
  }
}

customElements.define('app-nav', AppNav)
